import React, { ReactNode, useState, useEffect } from "react";
import { Users, UserCheck, X } from "lucide-react";
import { loadOrganizationLogos } from "../../../utils";
import { getIconUrl } from "../../../iconUtils";
import "./style.css"; // contains existing + modal CSS


interface Auditor {
  name: string;
  email: string;
}

interface AuditTheme {
  themeName: string;
  type: string;
  countryCode: string;
  themeCode: string;
}

interface AuditeeLead {
  name: string;
  email: string;
}

interface AuditOrganization {
  organizationId: number;
  name: string;
  logo?: any;
  organizationLogo?: any;
  type: string;
  auditeeLead: AuditeeLead[];
  applications: string[];
  totalMembers: Auditor[];
  auditeeMembers: Auditor[];
  participatedAuditeeMembers: Auditor[];
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
}

interface Theme {
  id: number;
  name: string;
  color: string;
  icon?: ReactNode;
}

interface Organization {
  name: string;
  application: any;
  icon: ReactNode;
  iconBg: string;
  teamLead: string;
  auditeeCount: number;
  participated: number;
  participatedAuditeeMembers: Auditor[];
  auditeeMembers: Auditor[];
}

interface AuditResourcesProps {
  auditId: string;
  auditTeam: {
    auditor: Auditor[];
    auditTheme: AuditTheme[];
    auditOrganizations: AuditOrganization[];
  };
  scope: string;
  auditType: string;
  status: string;
  auditorMembers: TeamMember[];
  auditeeMembers: TeamMember[];
}

const GeneralInfoAuditCard: React.FC<AuditResourcesProps> = ({ auditTeam }) => {
  const [activeModal, setActiveModal] = useState<{
    type: "total" | "participated" | null;
    org: Organization | null;
  }>({ type: null, org: null });
  const [processedOrganizations, setProcessedOrganizations] = useState<
    AuditOrganization[]
  >([]);

  const closeModal = () => setActiveModal({ type: null, org: null });

  // Process organizations to convert logo data to URLs
  useEffect(() => {
    const processOrganizationsWithLogos = async () => {
      const organizations = auditTeam?.auditOrganizations;
      if (organizations && organizations.length > 0) {
        // Map organizations to match the utility function's expected format
        const mappedOrgs = organizations.map((org) => ({
          ...org,
          organizationLogo: org.organizationLogo || org.logo,
        }));
        const logoMap = await loadOrganizationLogos(mappedOrgs);
        const orgsWithLogos = organizations.map((org) => ({
          ...org,
          logo: logoMap.get(org.organizationId) || org.organizationLogo || org.logo,
        }));
        setProcessedOrganizations(orgsWithLogos);
      } else {
        setProcessedOrganizations(organizations || []);
      }
    };

    processOrganizationsWithLogos();
  }, [auditTeam?.auditOrganizations]);

  const teamMembers: TeamMember[] =
    auditTeam?.auditor?.map((auditor, index) => ({
      id: index + 1,
      name: auditor.name,
      email: auditor.email,
    })) || [];

  const themes: Theme[] =
    auditTeam?.auditTheme?.map((theme, index) => ({
      id: index + 1,
      name: `${theme.themeName} (${theme.themeCode})`,
      color: theme.type === "policy" ? "#ef4444" : "#7c3aed",
      icon: theme.countryCode ? (
        <img
          src={getIconUrl(theme.countryCode.toLowerCase())}
          alt={`${theme.countryCode} flag`}
          className="giac-theme-flag"
        />
      ) : null,
    })) || [];

  const organizations: Organization[] =
    processedOrganizations?.map((org) => ({
      name: org.name,
      application: org.applications?.join(", ") || "N/A",
      icon: org.logo ? (
        <img src={org.logo} alt={org.name} className="giac-org-img" />
      ) : null,
      iconBg: "white",
      teamLead: org.auditeeLead?.[0]?.name || "N/A",
      auditeeCount: org.auditeeMembers?.length || 0,
      participated: org.participatedAuditeeMembers?.length || 0,
      totalMembers: org.totalMembers,
      auditeeMembers: org.auditeeMembers,
      participatedAuditeeMembers: org.participatedAuditeeMembers || [],
    })) || [];

  return (
    <div className="flex-wrap gap-3">
      <div className="giac-wrapper">
        <h1 className="giac-title">Audit Resources & Themes</h1>

        <div className="giac-grid">
          {/* Auditor Team Members */}
          <div className="giac-card">
            <h2 className="giac-section-title">Auditor Team Members</h2>
            <div className="grid-scroll">
              <table className="giac-table">
                <tbody>
                  {teamMembers.length > 0 ? (
                    teamMembers.map((member) => (
                      <tr key={member.id}>
                        <td>{member.id}</td>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="giac-empty">
                        No team members found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Audit Themes */}
          <div className="giac-card">
            <h2 className="giac-section-title">Audit Themes</h2>
            <div className="grid-scroll">
              {themes.map((theme) => (
                <div key={theme.id} className="giac-theme-row">
                  {theme.icon && (
                    <span className="giac-theme-icon">{theme.icon}</span>
                  )}
                  <span className="giac-theme-name">{theme.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Organization Overview */}
        <h2 className="giac-org-title">Organization Overview</h2>
        <div className="giac-org-grid">
          {organizations.map((org, index) => (
            <div key={index} className="giac-card">
              <div className="giac-org-header">
                <div
                  className="giac-org-icon"
                  style={{ backgroundColor: org.iconBg }}
                >
                  {org.icon}
                </div>
                <div>
                  <div className="giac-org-name">{org.name}</div>
                  <div className="giac-org-app">
                    Application: {org.application}
                  </div>
                </div>
              </div>

              <div className="giac-org-team">
                <span className="giac-org-team-label">Auditee Team Lead:</span>
                <span className="giac-org-team-value">{org.teamLead}</span>
              </div>

              <div className="giac-org-stats">
                <div
                  className="giac-stat-item"
                  onClick={() => setActiveModal({ type: "participated", org })}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setActiveModal({ type: "participated", org }); }}
                  role="button"
                  tabIndex={0}
                >
                  <Users size={20} className="giac-stat-icon" />
                  <div className="giac-stat-label">Auditee Count</div>
                  <div className="giac-stat-value">{org.auditeeCount}</div>
                </div>

                <div
                  className="giac-stat-item"
                  onClick={() => org.participated !== 0 && setActiveModal({ type: "total", org })}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && org.participated > 0) setActiveModal({ type: "total", org }); }}
                  role="button"
                  tabIndex={0}
                >
                  <UserCheck size={20} className="giac-stat-icon" />
                  <div className="giac-stat-label">Participated</div>
                  <div className="giac-stat-value">{org.participated}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {activeModal.type && activeModal.org && (
        <>
          <div className="giac-modal-backdrop" onClick={closeModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                closeModal();
              }
            }}
            role="button"
            tabIndex={0}></div>
          <div className="giac-modal">
            <div className="giac-modal-header">
              <h3 className="giac-modal-title">
                {activeModal.type === "total"
                  ? "Participated Auditee Members"
                  : "Total Auditee Members"}
              </h3>

              <button className="giac-modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="giac-alignment">
              <div className="giac-modal-content">
                {(activeModal.type === "total" ? activeModal.org.participatedAuditeeMembers : activeModal.org.auditeeMembers
                ).map((auditee, index) => (
                  <div key={index} className="giac-modal-item">
                    <span className="giac-modal-index">{index + 1}.</span>
                    <div>
                      <div className="giac-modal-name">{auditee.name}</div>
                      <div className="giac-modal-email">{auditee.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralInfoAuditCard;
